import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    AchBatchFilterModelDto,
    AchBatchesClient,
    AchClient,
    AchCompaniesClient,
    AchFileActivityClient,
    AchPaymentFilterModelDto,
    AchPaymentModelDto,
    AchPaymentsClient,
    AchRecipientFilterModelDto,
    AchRecipientsClient,
    AchSettingsClient,
    CompanyAccountsClient,
    HolidaysClient,
    SecCodesClient,
} from '@treasury/api/channel';
import { CutoffTimes } from '../channel/types';
import { SameDayAchSettings, getDefaultValueDate } from '../channel/types/ach';
import {
    AchBatchFilter,
    AchCompany,
    AchFile,
    AchFileActivity,
    AchFileActivityFilter,
    AchFileError,
    AchPayment,
    AchRecipientRecord,
    AchSource,
    AchTemplate,
    OffsetAccount,
} from './';

@Injectable()
export class AchService {
    constructor(
        private readonly batchClient: AchBatchesClient,
        private readonly companyClient: AchCompaniesClient,
        private readonly companyAccountsClient: CompanyAccountsClient,
        private readonly secCodesClient: SecCodesClient,
        private readonly achPaymentsClient: AchPaymentsClient,
        private readonly achSettingsClient: AchSettingsClient,
        private readonly holidaysClient: HolidaysClient,
        private readonly achRecipientsClient: AchRecipientsClient,
        private readonly achClient: AchClient,
        private readonly achFileActivityClient: AchFileActivityClient
    ) {}

    public async getAchCutoffTimes() {
        const dto = (await this.companyAccountsClient.companyAccountsGetCutoffTimes2('SameDayAch', {
            maxAgeInSeconds: 600,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        })) as unknown as any;

        return dto.data;
    }

    public async getCutoffTimes() {
        const dto = await this.companyAccountsClient.companyAccountsGetCutoffTimes({
            maxAgeInSeconds: 600,
        });
        return dto.data;
    }

    public async getNextAvailableAchDay() {
        const holidays = (
            await this.holidaysClient.holidaysGet({ maxAgeInSeconds: 60000 })
        ).data.map(h => ({
            date: new Date(h.date),
        }));
        const sameDayAchSettings = (
            await this.companyAccountsClient.companyAccountsGetCutoffTimes2('SameDayAch', {
                maxAgeInSeconds: 600,
            })
        ).data;
        const allowSameDayPayments = sameDayAchSettings.processingCutoff?.allowSameDay ?? false;
        const cutoffTimes = await this.getCutoffTimes();

        const date = getDefaultValueDate(
            allowSameDayPayments,
            cutoffTimes as CutoffTimes,
            holidays,
            sameDayAchSettings as SameDayAchSettings
        );
        // This check for $TODAY can be removed wihen Omega reaches EOL and the 'getDefaultValueDate' function is updated to return an ISO date string
        return date === '$TODAY' ? new Date() : new Date(date);
    }

    public async getSettings() {
        return (
            await this.achSettingsClient.achSettingsGet({
                maxAgeInSeconds: 6000,
            })
        ).data;
    }

    public async getTemplates(filters: AchBatchFilter) {
        const dtos = (
            await this.batchClient.achBatchesPostAll(filters.toDto() as AchBatchFilterModelDto, {
                maxAgeInSeconds: 600,
            })
        ).data;
        return dtos.map(dto => new AchTemplate(dto));
    }

    public async getTemplateById(id: string) {
        const dto = (
            await this.batchClient.achBatchesGet(Number.parseInt(id), { maxAgeInSeconds: 600 })
        ).data;
        return new AchTemplate(dto.batch);
    }

    public async deleteTemplate(id: number) {
        return this.batchClient.achBatchesDelete({
            achBatchId: id,
            securityMessage: {
                actionType: 'Delete Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
    }

    public async getOffsetAccountsByACHCompanyId(id: number) {
        const dtos = (
            await this.companyClient.achCompaniesOffsetAccounts(id, undefined, {
                maxAgeInSeconds: 600,
            })
        ).data;
        return dtos.map(d => new OffsetAccount(d));
    }

    public async getAchCompanies() {
        const dtos = (await this.companyClient.achCompaniesGet({ maxAgeInSeconds: 600 })).data;
        return dtos.map(c => new AchCompany(c));
    }

    public async getSecCodes() {
        const response = await this.secCodesClient.secCodesGet({ maxAgeInSeconds: 600 });
        if (!response.data.includes('All')) {
            response.data.unshift('All');
        }
        return response.data;
    }

    public async getPaymentById(id: string) {
        const dto = (await this.achPaymentsClient.achPaymentsGet2(Number.parseInt(id))).data;
        return new AchPayment(dto.payment);
    }

    public async createPayment(
        template: AchTemplate,
        applyUpdatesToBatch: boolean,
        duplicatePreventionId: string
    ) {
        const dto = await this.achPaymentsClient.achPaymentsPost({
            ...template.toPaymentDto(template),
            applyUpdatesToBatch,
            duplicatePreventionId,
        } as unknown as AchPaymentModelDto);

        return new AchPayment(dto.data.payment as unknown as AchPaymentModelDto);
    }

    public async updatePayment(template: AchTemplate, applyUpdatesToBatch: boolean) {
        const dto = await this.achPaymentsClient.achPaymentsPut(template.toDto().id, {
            ...template.toDto(),
            applyUpdatesToBatch,
        } as unknown as AchPaymentModelDto);

        return new AchPayment(dto.data as unknown as AchPaymentModelDto);
    }

    public async getPayments(type: string, filters: AchPaymentFilterModelDto) {
        const response = await this.achPaymentsClient.achPaymentsPostAll3({ type }, filters);
        return response.data.map(payment => new AchPayment(payment));
    }

    public async getPaymentsAndTotals(type: string, filters: AchPaymentFilterModelDto) {
        const response = await this.achClient.achPaymentsSearchAchPayments({ type }, filters);
        return {
            payments: response.data.results?.map(payment => new AchPayment(payment)),
            totals: response.data.totals,
        };
    }

    public async getRecipients(filters: AchRecipientFilterModelDto) {
        const response = await this.achRecipientsClient.achRecipientsPost(filters);
        const { limitedPayments, limitedBatches, achRecipientRecords } = response.data;

        return {
            achRecipients: achRecipientRecords
                ? achRecipientRecords.map(recipient => new AchRecipientRecord(recipient))
                : undefined,
            limitedPayments,
            limitedBatches,
        };
    }

    public async approveTemplate(template: AchTemplate, comments?: string) {
        const response = await this.batchClient.achBatchesApprove(template.templateId, {
            key: template.templateId as unknown as string,
            value: comments,
            securityMessage: {
                actionType: 'Approve Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
        return response.data;
    }

    public async rejectTemplate(template: AchTemplate, comments?: string) {
        const response = await this.batchClient.achBatchesReject(template.templateId, {
            key: template.templateId as unknown as string,
            value: comments,
            securityMessage: {
                actionType: 'Reject Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
        return response.data;
    }

    public async approveAchPayments(ids: string[], comments: string) {
        return this.achPaymentsClient.achPaymentsBatch(
            { action: 'Approve' },
            {
                lookups: ids.map(id => ({
                    key: id,
                    value: comments,
                    id: 0,
                    updatedDate: '',
                })),
                securityMessage: {
                    actionType: 'Approve ACH Payment',
                    status: undefined,
                    methodUsed: undefined,
                    hasAlternate: false,
                    errorCode: undefined,
                    message: undefined,
                    oneTimePassword: undefined,
                    challengeMethodTypeId: 0,
                },
                id: 0,
                updatedDate: '',
            }
        );
    }

    public async rejectAchPayments(ids: string[], comments: string) {
        return this.achPaymentsClient.achPaymentsBatch(
            {
                action: 'Reject',
            },
            {
                lookups: ids.map(id => ({
                    key: id,
                    value: comments,
                    id: 0,
                    updatedDate: '',
                })),
                securityMessage: {
                    actionType: 'Reject ACH Payment',
                    status: undefined,
                    methodUsed: undefined,
                    hasAlternate: false,
                    errorCode: undefined,
                    message: undefined,
                    oneTimePassword: undefined,
                    challengeMethodTypeId: 0,
                },
                id: 0,
                updatedDate: '',
            }
        );
    }

    // ACH File Activity methods

    public async getAchFileActivity(filters: AchFileActivityFilter) {
        const response = await this.achFileActivityClient.achFileActivityPostAll(filters.toDto());
        return response.data.map(dto => new AchFileActivity(dto));
    }

    public async getAchFileContents(fileArchiveId: number) {
        const response =
            await this.achFileActivityClient.achFileActivityGetAchFileContent(fileArchiveId);
        return new AchFile(response.data);
    }

    public async getAchFileErrorDetails(fileArchiveId: number) {
        const response =
            await this.achFileActivityClient.achFileActivityGetAchFileError(fileArchiveId);
        const errorModel = new AchFileError(response.data);
        return errorModel.validationErrors;
    }

    public async getAchFileSources() {
        const response = await this.achFileActivityClient.achFileActivityGetAchSourceList();
        return response.data.map(dto => new AchSource(dto));
    }

    public async getAchProcessedByUsers() {
        const response =
            await this.achFileActivityClient.achFileActivityGetProcessedByFilterUsers();
        return response.data;
    }

    public async deleteAchFile(fileId: number) {
        return this.achFileActivityClient.achFileActivityDelete(fileId);
    }

    public async processAchFile(fileId: number) {
        const response = await this.achFileActivityClient.achFileActivityPost(fileId);
        return new AchPayment(response.data);
    }

    public async downloadNachaFileContent(fileArchiveId: number) {
        return this.achFileActivityClient.achFileActivityGetNachaFileContent(fileArchiveId);
    }
}
